import { action, computed } from 'mobx'
import { type InventoryItem } from '@/features/inventory/InventoryItem'
import {
  getInventoryItemPriceDisplay,
  getInventoryItemPriceTypeDisplayName,
  getInventoryItemTypeDisplayName,
} from '@/features/inventory/inventory-item-display'

/**
 * The actions a row offers on its service item.
 */
export interface InventoryItemRowActions {
  edit: (item: InventoryItem) => void
  override: (item: InventoryItem) => void
  archive: (item: InventoryItem) => void
  unarchive: (item: InventoryItem) => void
}

/**
 * The state of the service manager a row's actions depend on.
 */
export interface InventoryItemRowContext {
  isGlobalSelected: () => boolean
  canManageWorkspaceInventory: () => boolean
  hasWorkspace: () => boolean
}

/**
 * A row in the service manager table.
 */
export class InventoryItemRowViewModel {
  constructor(
    readonly inventoryItem: InventoryItem,
    private readonly _context: InventoryItemRowContext,
    private readonly _actions: InventoryItemRowActions,
  ) {}

  /**
   * Whether the row offers any action. An archived global item is only
   * managed from the global inventory.
   */
  @computed
  get hasActions() {
    return !(
      this.inventoryItem.global &&
      this.inventoryItem.archived &&
      !this._context.isGlobalSelected()
    )
  }

  /**
   * Whether the item can be edited, archived or unarchived here: global items
   * only from the global inventory.
   */
  @computed
  get canEdit() {
    return !this.inventoryItem.global || this._context.isGlobalSelected()
  }

  @computed
  get canOverride() {
    return (
      this.inventoryItem.global &&
      !this.inventoryItem.archived &&
      this._context.canManageWorkspaceInventory()
    )
  }

  @computed
  get canArchive() {
    return this.canEdit && !this.inventoryItem.archived
  }

  @computed
  get canUnarchive() {
    return this.canEdit && this.inventoryItem.archived
  }

  /**
   * Whether a workspace's inventory is shown, so an override is for it.
   */
  @computed
  get hasWorkspace() {
    return this._context.hasWorkspace()
  }

  @computed
  get serviceTypeText() {
    return getInventoryItemTypeDisplayName(
      this.inventoryItem.typeSpecification.type,
    )
  }

  @computed
  get priceTypeText() {
    return getInventoryItemPriceTypeDisplayName(this.inventoryItem)
  }

  @computed
  get clientPrice() {
    return getInventoryItemPriceDisplay(this.inventoryItem, 'Client')
  }

  @computed
  get proPrice() {
    return getInventoryItemPriceDisplay(this.inventoryItem, 'Pro')
  }

  @action.bound
  edit() {
    this._actions.edit(this.inventoryItem)
  }

  @action.bound
  override() {
    this._actions.override(this.inventoryItem)
  }

  @action.bound
  archive() {
    this._actions.archive(this.inventoryItem)
  }

  @action.bound
  unarchive() {
    this._actions.unarchive(this.inventoryItem)
  }
}
